<script setup>
const route = useRoute();
const slugs = ref(route.params.slug);
const jobId = ref(slugs.value[0]);
let data, error;
try {
  ({ data, error } = await useCmsFetch(`entry/${jobId.value}`));
} catch {
  await navigateTo('/join-us', { redirectCode: 301 });
}

if (error?.value || !data?.value) {
  await navigateTo('/join-us', { redirectCode: 301 });
}

const { breadcrumbs } = useTTCState();

onMounted(() => {
  breadcrumbs.value = data.value.breadcrumbs;
});

onBeforeUnmount(() => {
  breadcrumbs.value = null;
});
const { isMobileOrTablet } = useDevice();
const { width: innerWidth } = useWindowSize();

const isMobile = computed(() => {
  if (process.server) return isMobileOrTablet;
  return innerWidth.value < 900;
});

const scrollToSignup = function () {
  const target = document.getElementById("job-sign-up");
  if (process.client) {
    if (target) {
      const yTo = window.scrollY + target.getBoundingClientRect().top;
      window.scrollTo({
        top: yTo - 50,
        behavior: "smooth",
      });
    }
  }
};

// -------- SMART RECRUITERS STUFF --------
const { getJob, getJobSeoImage } = useSmartRecruiters();
const { data: smartData, error: smartError } = await getJob(jobId.value);

if (smartError.value || !smartData.value) {
  await navigateTo('/join-us', { redirectCode: 301 });
}

const seoImage = ref(getJobSeoImage(smartData.value));

// Single fallback chain for all job page titles (document, hero, form)
const jobTitle = computed(
  () =>
    data.value?.seoTitle ||
    data.value?.headline ||
    data.value?.meta?.title ||
    smartData.value?.name ||
    "",
);

const pageTitle = computed(() =>
  jobTitle.value
    ? `${jobTitle.value} | .the tech collective`
    : ".the tech collective",
);

const config = useRuntimeConfig();
const canonicalUrl = config.public.URL ? config.public.URL + useRoute().path : useRequestURL().href;

useHead({
  title: pageTitle,

  meta: [
    { property: "og:title", content: jobTitle },
    { name: "image", property: "og:image", content: seoImage.value },
    { name: "description", content: data.value?.seoDescription },
    { name: "robots", content: "noindex, follow" },
  ],
  link: [
    {
      rel: "canonical",
      href: canonicalUrl,
    },
  ].filter((item) => item.href),
});

const formWrapper = ref(null);
const { height: formWrapperheight } = useElementSize(formWrapper);

const { setGlobalTheme } = useTheme();
onMounted(() => {
  setGlobalTheme(data.value.theme);
});
</script>

<template>
  <div v-if="data">
    <div class="relative">
      <div class="grid w-screen gap-4 lg:grid-cols-2 lg:px-bleed">
        <div
          class="relative"
          :style="{ 'min-height': `calc(${formWrapperheight}px + 100dvh)` }"
        >
          <section-hero-job
            class="min-h-screen lg:sticky lg:top-14"
            :headline="jobTitle"
            :trumpet="data.trumpet"
            :subHeading="data.subHeading"
            @signupClick="scrollToSignup"
          />

          <div
            v-if="!isMobile"
            ref="formWrapper"
            class="form-wrapper top-screen absolute bottom-0 z-[1] bg-dark-grey p-bleed lg:w-[var(--cols-6)] lg:p-0"
          >
            <div class="py-bleed">
              <div class="relative p-5" id="job-sign-up" ref="form">
                <ui-tag-corners />
                <client-only>
                  <component-element-job-form
                    :jobId="jobId"
                    :jobTitle="jobTitle"
                    :postingUuid="data.meta.jobPostingUuid"
                /></client-only>
              </div>
            </div>
          </div>
        </div>

        <div
          class="relative flex flex-col gap-20 px-bleed lg:min-h-[200vh] lg:px-0"
        >
          <div class="rte-wrapper">
            <component-element-rich-text
              v-if="smartData?.jobAd.sections.jobDescription.text"
              :rte="smartData?.jobAd.sections.jobDescription.text"
            />
          </div>
          <component-sections
            v-if="data.jobSections.length > 0"
            :sections="data.jobSections"
            :isHalf="true"
          />
          <div v-if="isMobile" class="bg-dark-grey">
            <div class="py-bleed">
              <div class="relative p-5" id="job-sign-up" ref="form">
                <ui-tag-corners />
                <client-only>
                  <component-element-job-form
                    :jobId="jobId"
                    :jobTitle="jobTitle"
                    :postingUuid="data.meta.jobPostingUuid"
                /></client-only>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <globals-event-job-footer
      :job-event-data="data.latestJobs"
      type="job"
      v-if="route.name === 'job-slug'"
    />
  </div>
</template>

<style scoped>
.rte-wrapper {
  @media (min-width: 900px) {
    margin-top: 50%;
  }
}
</style>
